import { DARK_THEME_IDS, THEMES, ThemeId } from '@/theme/tokens';
import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = ThemeId | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  /** theme with 'system' resolved to a concrete theme id */
  resolvedTheme: ThemeId;
  setTheme: (theme: Theme) => void;
};

const VALID_THEMES = new Set<string>([...THEMES.map((t) => t.id), 'system']);

export function resolveTheme(theme: Theme): ThemeId {
  if (theme === 'system') {
    const isDark =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    return isDark ? 'dark' : 'light';
  }
  return theme;
}

const initialState: ThemeProviderState = {
  theme: 'light',
  resolvedTheme: 'light',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey);
    return stored && VALID_THEMES.has(stored)
      ? (stored as Theme)
      : defaultTheme;
  });

  const resolvedTheme = resolveTheme(theme);

  useEffect(() => {
    const root = window.document.documentElement;
    const isDark = DARK_THEME_IDS.includes(resolvedTheme);

    localStorage.setItem(storageKey, theme);
    // 'dark'/'light' classes drive Tailwind's `dark:` variants and legacy
    // selectors; data-theme drives the token blocks in tailwind.css.
    root.classList.remove('light', 'dark');
    root.classList.add(isDark ? 'dark' : 'light');
    root.setAttribute('data-theme', resolvedTheme);
  }, [storageKey, theme, resolvedTheme]);

  return (
    <ThemeProviderContext.Provider
      {...props}
      value={{
        theme,
        resolvedTheme,
        setTheme,
      }}
    >
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};

export const useIsDarkTheme = () => {
  const { resolvedTheme } = useTheme();

  return DARK_THEME_IDS.includes(resolvedTheme);
};
